import {Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch} from '@stencil/core';
import {ButtonSizes, DialogSizes} from '@/types';
import {preventBodyScroll} from '@/utils';
/**
 * @slot dialog-header - The dialog title
 * @slot dialog-subheader - The dialog subtitle
 * @slot dialog-body - The dialog main content
 * @slot dialog-cancel - If you need to customize the cancel CTA (with any attribute for a specific need), use this slot
 * @slot dialog-confirm - If you need to customize the confirm CTA (with any attribute for a specific need), use this slot
 */
@Component({
    tag: 'joy-dialog',
    styleUrls: ['./style/dialog.scss'],
    shadow: true,
})
export class Dialog {
    private dialogChain = false;
    private fireCancelEvent = true;
    private get cancelCTASlot(): HTMLElement[] {
        return Array.from(this.host.querySelectorAll('[slot="dialog-cancel"]'));
    }

    private get confirmCTASlot(): HTMLElement[] {
        return Array.from(this.host.querySelectorAll('[slot="dialog-confirm"]'));
    }

    @State() hasFooterSlot = false;
    @Element() host!: HTMLJoyDialogElement;

    /** Dialog open state */
    @Prop({reflect: true, mutable: true}) open = false;
    /** Dialog sizes */
    @Prop({reflect: true}) size: DialogSizes = 'default';
    /** Main CTA text. If none given, it won't show the CTA */
    @Prop({reflect: true}) confirmText?: string;
    /** Secondary CTA text. If none given, it won't show the CTA */
    @Prop({reflect: true}) cancelText?: string;
    /** If you want to change the CTA sizes, please choose between small, medium, large */
    @Prop({reflect: true}) buttonSize: ButtonSizes = 'small';
    /** Set the URL of the image you can inject as pre-header banner */
    @Prop({reflect: true}) bannerSrc?: string;
    /** Set the alt text of the banner */
    @Prop({reflect: true}) bannerSrcAlt?: string;
    /** Set the height of the banner. Default to 250 */
    @Prop({reflect: true}) bannerHeight = 250;
    /** Set the position of the banner image. Work exactly like css background-position property */
    @Prop({reflect: true}) bannerPosition = 'center';
    /** Loads a preset state for dialog, preventing closing the dialog "accidentally" by clicking on cross, backdrop or pressing Escape key. */
    @Prop({reflect: true}) patternConfirm = false;

    /** Custom event fired when clicking on confirm button */
    @Event({eventName: 'joy-confirm-dialog'}) joyConfirmDialog!: EventEmitter<void>;
    /** Custom event fired when clicking on cancel button (even when slotted) or top right cross icon */
    @Event({eventName: 'joy-cancel-dialog'}) joyCancelDialog!: EventEmitter<void>;

    @Listen('keydown', {target: 'document'})
    closeOnEscapePress(ev: any) {
        if (ev.code === 'Escape' && this.open && !this.patternConfirm) {
            this.onClose();
        }
    }

    @Listen('joy-indicators-change')
    async onStepChange() {
        this.open = false;
    }

    @Watch('open')
    async closeDialogIfNeeded(newValue: boolean, oldValue: boolean) {
        // When we click on dismiss CTAs, we use the same onClose method.
        // In order not to trigger the same method 2 times, we close it only if it was previously opened (thanks to the oldValue param).
        if (oldValue && !newValue) {
            this.onClose();
            if (this.fireCancelEvent) {
                this.joyCancelDialog.emit();
            }
        } else if (newValue) {
            await this.openDialog();
        }
        this.fireCancelEvent = true;
    }

    /**
     * If you want to trigger specific action after the dialog opening.
     * @param {Function} callback
     */
    @Method()
    async openDialog(callback?: () => any) {
        preventBodyScroll(true);
        this.dialogChain = this.dialogChainOnGoing();
        this.open = true;
        this.host.focus();

        if (this.cancelCTASlot) {
            this.cancelCTASlot.forEach((cancel) => cancel.addEventListener('click', this.closeDialogListener));
        }

        if (callback) {
            callback();
        }
    }

    private closeDialogListener = async () => {
        return await this.closeDialog();
    };

    /**
     * Hide the dialog from outside
     * @return {Promise}
     */
    @Method()
    async closeDialog({fireCancelEvent} = {fireCancelEvent: true}): Promise<void> {
        this.fireCancelEvent = fireCancelEvent;
        return this.onClose();
    }

    private onClose = () => {
        this.open = false;
        preventBodyScroll(false);
        this.focusOnTriggerWhenClose();

        if (this.cancelCTASlot) {
            this.cancelCTASlot.forEach((cancel) => cancel.removeEventListener('click', this.closeDialogListener));
        }
    };

    private dialogChainOnGoing() {
        return !!document.body.querySelector('joy-dialog.joy-dialog--open');
    }

    private focusOnTriggerWhenClose() {
        const trigger = document.querySelector(`joy-dialog-trigger[dialog="${this.host.id}"] button`);

        // Of course, it won't focus on anything if the dialog is open by default without any trigger
        if (trigger) {
            (trigger as HTMLElement).focus();
        }
    }

    private dialogHasButtons() {
        const buttonSlots = [...this.cancelCTASlot, ...this.confirmCTASlot];
        const hasButtonProps = !!this.cancelText || !!this.confirmText;

        return buttonSlots.length > 0 || hasButtonProps;
    }

    private fireConfirmEvent() {
        this.joyConfirmDialog.emit();
    }

    connectedCallback() {
        this.closeDialog = this.closeDialog.bind(this);
        this.fireConfirmEvent = this.fireConfirmEvent.bind(this);
    }

    async componentWillLoad() {
        if (this.open) {
            await this.openDialog();
        }

        this.confirmCTASlot.forEach((confirmCta) => {
            confirmCta.addEventListener('click', this.fireConfirmEvent);
        });
    }

    disconnectedCallback() {
        this.confirmCTASlot.forEach((confirmCta) => {
            confirmCta.removeEventListener('click', this.fireConfirmEvent);
        });

        preventBodyScroll(false);
        if (this.cancelCTASlot) {
            this.cancelCTASlot.forEach((cancel) => cancel.removeEventListener('click', this.closeDialogListener));
        }
    }

    render() {
        return (
            <Host role="dialog" aria-modal="true">
                <div
                    class={{
                        'joy-dialog__overlay': true,
                        'joy-dialog__overlay--open': this.open,
                        'joy-dialog__overlay___disabled': this.patternConfirm,
                    }}
                    onClick={() => !this.patternConfirm && this.onClose()}
                ></div>
                <div
                    class={{
                        'joy-dialog--wrapper': true,
                        'joy-dialog--wrapper-open': this.open,
                    }}
                >
                    <div class="joy-dialog">
                        {this.bannerSrc && (
                            <div
                                class="joy-dialog--banner"
                                style={{
                                    backgroundImage: `url(${this.bannerSrc})`,
                                    backgroundPosition: this.bannerPosition,
                                    height: `${this.bannerHeight}px`,
                                }}
                            />
                        )}
                        <div class="joy-dialog--header">
                            <div class="joy-dialog--header__title">
                                <slot name="dialog-header" />
                            </div>
                            <p class="joy-dialog--subheader" id="dialogDescription">
                                <slot name="dialog-subheader" />
                            </p>
                            {!this.patternConfirm && (
                                <joy-icon id="joy-dialog--close" tabindex="1" name="cross" class="joy-dialog--close" onClick={() => this.onClose()} />
                            )}
                        </div>
                        <div class="joy-dialog--body">
                            <slot name="dialog-body" />
                        </div>
                        <div
                            class={{
                                'joy-dialog--footer': true,
                                'joy-dialog--footer-filled': this.dialogHasButtons(),
                            }}
                        >
                            <slot name="dialog-cancel">
                                {this.cancelText && (
                                    <joy-button onClick={() => this.onClose()} class="joy-dialog--cancel" variant="ghost" size={this.buttonSize}>
                                        {this.cancelText}
                                    </joy-button>
                                )}
                            </slot>

                            <slot name="dialog-confirm">
                                {this.confirmText && (
                                    <joy-button onClick={this.fireConfirmEvent} class="joy-dialog--confirm" variant="main" size={this.buttonSize}>
                                        {this.confirmText}
                                    </joy-button>
                                )}
                            </slot>
                        </div>
                        <div
                            class={{
                                'joy-dialog--indicators': true,
                            }}
                        >
                            <slot name="dialog-indicators" />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
